import NavigationHelper from 'layers/navigation/navigationHelper'

export const STANDALONE_ADMIN_PROGRAMS = new Set(['bpu', 'cpu', 'dwb', 'sela'])

export const normalizePath = path => path?.replace(/\/$/, '').split('?')[0]

export const resolveProgramSlug = (programName, pathname) =>
  programName?.toLowerCase() ||
  pathname.match(/\/admin\/program\/([^/]+)/)?.[1]?.toLowerCase()

/** Standalone programs (DWB, BPU, etc.) live at /admin/program/{slug} with no view segment. */
export const isStandaloneFinalUrl = (pathname, slug, viewOption) => {
  const program = slug?.toLowerCase()
  if (viewOption || !program || !STANDALONE_ADMIN_PROGRAMS.has(program)) {
    return false
  }
  return normalizePath(pathname) === `/admin/program/${program}`
}

export const pathsMatch = (pathname, targetUrl) =>
  normalizePath(pathname) === normalizePath(targetUrl)

export const navigateIfDifferent = (navigationEvent, pathname, targetUrl) => {
  if (targetUrl && !pathsMatch(pathname, targetUrl)) {
    navigationEvent(targetUrl, NavigationHelper.types.REPLACE)
  }
}

export const programTitlesChanged = (current, next) =>
  !current?.length ||
  current.length !== next.length ||
  current.some((title, i) => title !== next[i])

const buildProgramAccess = (
  claims,
  hasAccessToSelaProgram,
  hasStandaloneDwbAccess,
) => ({
  k8: claims?.hasElemAdminClaim || claims?.hasMsAdminClaim,
  hs: claims?.hasHighschoolAdminClaim,
  cpu: claims?.hasCpuAdminClaim,
  bpu: claims?.hasBpuAdminClaim,
  sela: hasAccessToSelaProgram,
  dwb: hasStandaloneDwbAccess,
})

export const canUseRecentLeaderDashboardUrl = (
  recentDashboardUrl,
  claims,
  programSlug,
  hasAccessToSelaProgram,
  hasStandaloneDwbAccess,
) => {
  if (!recentDashboardUrl) return false

  const recent = recentDashboardUrl
    .match(/program\/([^/]+)/)?.[1]
    ?.toLowerCase()
  const access = buildProgramAccess(
    claims,
    hasAccessToSelaProgram,
    hasStandaloneDwbAccess,
  )

  if (!recent || !access[recent]) return false

  const current = programSlug?.toLowerCase()
  if (current && access[current]) return false

  return true
}

export const getRecentDashboardTargetUrl = ({
  recentDashboardUrl,
  pathname,
  search,
  claims,
  programSlug,
  hasAccessToSelaProgram,
  hasStandaloneDwbAccess,
}) => {
  if (
    !canUseRecentLeaderDashboardUrl(
      recentDashboardUrl,
      claims,
      programSlug,
      hasAccessToSelaProgram,
      hasStandaloneDwbAccess,
    )
  ) {
    return null
  }

  const target = `/admin/${recentDashboardUrl}${search}`
  return pathsMatch(pathname, target) ? null : target
}

export const extractViewOptionFromRecentUrl = recentDashboardUrl =>
  recentDashboardUrl?.split('/')?.[2] || null

/** Returns `program/{slug}` for standalone programs, otherwise null. */
export const toStandaloneProgramRoute = slug => {
  const program = slug?.toLowerCase()
  return STANDALONE_ADMIN_PROGRAMS.has(program) ? `program/${program}` : null
}
